import { Extension } from '@tiptap/core';
import { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

export type JsonTokenType = 'property' | 'string' | 'number' | 'keyword';

export type JsonToken = {
  start: number;
  end: number;
  type: JsonTokenType;
};

const STRING = /"(?:[^"\\]|\\.)*"/g;
const NUMBER = /-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/g;
const KEYWORD = /\b(?:true|false|null)\b/g;

// Colours the JSON tokens in a single text run. Strings are matched first and
// blanked out so digits/keywords inside a string aren't recoloured. A string
// followed by a colon is a property name.
export const tokenizeJson = (text: string): JsonToken[] => {
  const tokens: JsonToken[] = [];
  let masked = text;
  let match: RegExpExecArray | null;

  STRING.lastIndex = 0;
  while ((match = STRING.exec(text)) !== null) {
    const start = match.index;
    const end = start + match[0].length;
    const isProperty = /^\s*:/.test(text.slice(end));
    tokens.push({ start, end, type: isProperty ? 'property' : 'string' });
    masked = masked.slice(0, start) + ' '.repeat(end - start) + masked.slice(end);
  }

  for (const [regex, type] of [
    [NUMBER, 'number'],
    [KEYWORD, 'keyword'],
  ] as const) {
    regex.lastIndex = 0;
    while ((match = regex.exec(masked)) !== null) {
      tokens.push({ start: match.index, end: match.index + match[0].length, type });
    }
  }

  return tokens;
};

const TOKEN_CLASS: Record<JsonTokenType, string> = {
  property: 'json-token-property',
  string: 'json-token-string',
  number: 'json-token-number',
  keyword: 'json-token-keyword',
};

const buildDecorations = (doc: ProseMirrorNode): DecorationSet => {
  const decorations: Decoration[] = [];
  doc.descendants((node, pos) => {
    if (!node.isText || !node.text) {
      return;
    }
    for (const token of tokenizeJson(node.text)) {
      decorations.push(
        Decoration.inline(pos + token.start, pos + token.end, {
          class: TOKEN_CLASS[token.type],
        })
      );
    }
  });
  return DecorationSet.create(doc, decorations);
};

export const JsonHighlight = Extension.create({
  name: 'jsonHighlight',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('jsonHighlight'),
        props: {
          // Off in a code field — the browser flags JSON keys/values as typos.
          attributes: { spellcheck: 'false' },
          decorations(state) {
            return buildDecorations(state.doc);
          },
        },
      }),
    ];
  },
});
